'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useRef, useCallback } from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import PickerCircle from './PickerCircle';
import { hsvToRgb, rgbToHex } from './colorUtils';
const Container = styled(Box)(({ theme }) => ({
    width: '100%',
    height: '12px',
    flexShrink: 0,
    position: 'relative',
    marginTop: '16px',
    cursor: 'pointer',
    [theme.breakpoints.down('md')]: {
        width: '100%',
        maxWidth: '100%'
    },
    [theme.breakpoints.down('sm')]: {
        width: '100%',
        height: '8px',
        marginTop: '12px'
    }
}));
const Slider = styled(Box)(({ theme }) => ({
    width: '100%',
    height: '12px',
    flexShrink: 0,
    borderRadius: '24px',
    border: '1px solid rgba(0, 0, 0, 0.1)',
    background: 'linear-gradient(270deg, #ff0008 0%, #ff00eb 16.67%, #0e00ff 32.81%, #00edff 49.06%, #0f0 67.97%, #fffe00 83.12%, #ff0800 100%)',
    position: 'absolute',
    left: 0,
    top: 0,
    [theme.breakpoints.down('md')]: {
        width: '100%',
        maxWidth: '100%'
    },
    [theme.breakpoints.down('sm')]: {
        width: '100%',
        height: '8px'
    }
}));
const StyledPickerCircle = styled(PickerCircle)(({ position, theme }) => ({
    left: `${position - 16}px`,
    top: '-10px',
    cursor: 'grab',
    '&:active': {
        cursor: 'grabbing'
    },
    [theme.breakpoints.down('sm')]: {
        left: `${position - 12}px`,
        top: '-8px'
    }
}));
const HuePicker = ({ currentColor, onChange, huePosition, onHuePositionChange }) => {
    const containerRef = useRef(null);
    const handleMouseDown = useCallback((event) => {
        if (!containerRef.current)
            return;
        const rect = containerRef.current.getBoundingClientRect();
        const x = Math.max(0, Math.min(320, event.clientX - rect.left));
        const hue = Math.round((x / 320) * 360);
        const rgb = hsvToRgb(hue, currentColor.hsv.s, currentColor.hsv.v);
        const hex = rgbToHex(rgb.r, rgb.g, rgb.b);
        onHuePositionChange(x);
        onChange?.({
            hex,
            rgb,
            hsv: { h: hue, s: currentColor.hsv.s, v: currentColor.hsv.v }
        });
        const handleMouseMove = (moveEvent) => {
            const newX = Math.max(0, Math.min(320, moveEvent.clientX - rect.left));
            const newHue = Math.round((newX / 320) * 360);
            const newRgb = hsvToRgb(newHue, currentColor.hsv.s, currentColor.hsv.v);
            const newHex = rgbToHex(newRgb.r, newRgb.g, newRgb.b);
            onHuePositionChange(newX);
            onChange?.({
                hex: newHex,
                rgb: newRgb,
                hsv: {
                    h: newHue,
                    s: currentColor.hsv.s,
                    v: currentColor.hsv.v
                }
            });
        };
        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    }, [currentColor.hsv.s, currentColor.hsv.v, onChange, onHuePositionChange]);
    return (_jsxs(Container, { ref: containerRef, onMouseDown: handleMouseDown, children: [_jsx(Slider, {}), _jsx(StyledPickerCircle, { color: currentColor.hex, className: "picker-circle-hue", position: huePosition })] }));
};
export default HuePicker;
